import { computed, inject, onMounted, ref, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useApolloClient } from '@vue/apollo-composable'
import { APOLLO_CLIENTS } from 'common/dictionary.js'
import { useLoyaltyStore } from 'stores/loyalty'
import getContentList from '@/apollo/queries/getContentList.gql'
import getImageContentList from '@/apollo/queries/getImageContentList.gql'
import getSportsbookPromoFeed from '@/apollo/queries/getSportsbookPromoFeed.gql'

export function useMainSlider() {
  const { resolveClient } = useApolloClient()
  const apolloClient = resolveClient(APOLLO_CLIENTS.default)
  const apolloClientGo = resolveClient(APOLLO_CLIENTS.clientGo)

  const mediaQueries = inject('mediaQueries')
  const route = useRoute()
  const loyaltyStore = useLoyaltyStore()

  const isMobile = computed(() => mediaQueries?.mobileSmall ?? false)
  const group = computed(() => route.params.group)

  const allSlides = shallowRef([])
  const imageList = ref({})
  const coefficientsData = ref({})
  const isLoading = ref(true)
  const isError = ref(false)

  const mainPageSlides = computed(() =>
    allSlides.value.filter((slide) => !slide.loyalty_program || !loyaltyStore.isLoyaltyBanned),
  )

  async function loadBanners() {
    isLoading.value = true
    isError.value = false
    try {
      const { data } = await apolloClientGo.query({
        query: getContentList,
        variables: {
          type: 'banner',
          customFilters: [group.value ? group.value : 'main'],
        },
      })

      if (data) {
        await getSlides(data)
      } else {
        allSlides.value = []
      }
    } catch (e) {
      console.error(e)
      isError.value = true
      allSlides.value = []
    } finally {
      isLoading.value = false
    }
  }

  onMounted(loadBanners)

  watch(
    () => route.params.group,
    () => {
      allSlides.value = []
      imageList.value = {}
      coefficientsData.value = {}
      loadBanners()
    },
  )

  function extractMarkets(slides) {
    return slides.map((e) => e.eventId)?.filter((id) => typeof id === 'string') ?? []
  }

  async function getSlides(banners) {
    const propsContentList = banners.propsContentList ?? []
    const props = propsContentList
      .map((slide) => ({ id: slide.id, ...JSON.parse(slide.props) }))
      .filter((slide) => slide.active)
      .sort((a, b) => a.order - b.order)
    const listId = props.reduce((acc = [], current) => {
      function getId(elem) {
        if (elem?.id) {
          return elem.id
        } else if (typeof elem === 'number') {
          return elem
        } else {
          return null
        }
      }

      const array = [getId(current.image), getId(current.imageBackground), getId(current.imageTop)]
      acc.push(...array)
      return acc
    }, [])

    const uniqueListId = Array.from(new Set(listId)).filter((i) => i !== null)

    if (!uniqueListId.length) {
      return
    }

    getMarket(extractMarkets(props))

    const { data } = await apolloClientGo.query({
      query: getImageContentList,
      variables: { id: uniqueListId },
    })

    if (data?.imageContentList) {
      const newObject = {}
      data.imageContentList.forEach((i) => {
        newObject[i.id] = i
      })
      imageList.value = newObject
    }

    allSlides.value = props
    if (props.length <= 3 && isMobile.value && !props.showNavigationOnMobile) {
      allSlides.value = [...props, ...props.map((el) => ({ ...el, id: `${el.id}-clone` }))]
    }
  }

  async function getMarket(marketIds) {
    const tournamentIds = marketIds.filter((id) => id)
    if (!tournamentIds.length) {
      return
    }

    const { data } = await apolloClient.query({
      query: getSportsbookPromoFeed,
      variables: { tournamentIds },
    })

    if (data) {
      const sportData = JSON.parse(data.sportsbookPromoFeed)
      const filteredSportData = sportData.filter((el) => !!el)
      findMarket(filteredSportData)
    }
  }

  function findMarket(events) {
    let eventId
    events.forEach((event) => {
      event.forEach((option, index) => {
        if (index === 0) {
          eventId = option[index][0]
          coefficientsData.value[eventId] = []
        } else {
          coefficientsData.value[eventId].push({
            type: option[0][2],
            coefficients: [...Object.values(option[1][''])],
          })
        }
      })
    })
  }

  return {
    mainPageSlides,
    imageList,
    coefficientsData,
    isLoading,
    isError,
  }
}
